import { memo, useEffect, useRef } from 'react';
import { ProfileUserType } from '../../models/ClientModels';
import { makeDebug } from '../../utils/makeDebug';
import { isDebugEnabled } from '../debugging';
import { auth } from '../firebase/firebaseAccess';
import { useWatchValue } from '../hooks/usePrevious';
import { useCurrentUserId, useCurrentUserProfile } from './utils';

const debug = makeDebug('auth', isDebugEnabled('userAuth'));

export const useUpdateAuthTokenOnUserTypeChange = (userId: string) => {
  const profile = useCurrentUserProfile();
  const userType = profile?.userType;
  const timer = useRef<any>();

  useWatchValue(userType, async (prev, curr) => {
    // keep a local reference copy to the changed user
    const user = auth.currentUser;

    if (prev && curr && prev !== curr && user != null) {
      if (timer.current) {
        clearTimeout(timer.current);
      }

      if (__DEV__) {
        const idTokenResultPrev = await user.getIdTokenResult();
        debug(
          '[%s] user type changed from %s to %s, prev token info: %O',
          userId,
          prev,
          curr,
          idTokenResultPrev,
        );
      }

      let maxRetry = 10;

      const refresh = async () => {
        debug('[%s] refreshing id token ... ', userId);
        try {
          const res = await user.getIdTokenResult(true);
          if (res == null) {
            console.error('Refreshing id token returns null');
            return;
          }

          debug('[%s] refreshed id token result: %O', userId, res);

          // expect role change from normal user to seller take effect
          if (
            (curr !== ProfileUserType.normal &&
              !res.claims?.['roles']?.includes('seller')) ||
            (curr === ProfileUserType.normal &&
              res.claims?.['roles']?.includes('seller'))
          ) {
            if (--maxRetry > 0) {
              debug(
                '[%s] expect %s role in claims, retry refreshing... ',
                userId,
                curr === ProfileUserType.normal ? 'normal' : 'seller',
              );
              timer.current = setTimeout(refresh, 1000);
            } else {
              debug(
                '[%s] expect %s role in claims with no-lucky, aborted',
                userId,
                curr === ProfileUserType.normal ? 'normal' : 'seller',
              );
            }
          } else {
            debug('[%s] user id token refreshed successfully', userId);
          }
        } catch (e) {
          console.error('error refresh user id token on user type change: ', e);
        }
      };

      timer.current = setTimeout(refresh, 1000);
    }
  });

  useEffect(() => {
    debug('[%s] begin monitoring user type change... ', userId);
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = undefined;
      }
    };
  }, [userId]);
};

const UserTypeMonitor = memo(({ userId }: { userId: string }) => {
  useUpdateAuthTokenOnUserTypeChange(userId);
  return null;
});

export const RefreshUserTokenOnUserTypeChange = memo(() => {
  const userId = useCurrentUserId();
  if (!userId) return null;
  return <UserTypeMonitor key={userId} userId={userId} />;
});
